/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CatalogEntry, DataRoomTab } from "../state";

export const DataRoomTabs = defineComponent({
  name: "DataRoomTabs",
  props: {
    tabs: { type: Array, default: () => [] },
    activeTabId: { type: String, default: null },
    catalog: { type: Array, default: () => [] },
    onSelect: { type: Function, required: true },
    onClose: { type: Function, required: true },
    onNew: { type: Function, required: true },
  },
  setup(props: any) {
    const { t } = useLocalization();

    const titleFor = (tab: DataRoomTab): string => {
      if (tab.title) return tab.title;
      if (!tab.definitionId) return t("reports.dataroom.new_report");
      const entry = (props.catalog as CatalogEntry[]).find(
        (c) => c.definitionId === tab.definitionId,
      );
      return entry ? t(entry.nameKey) : tab.definitionId;
    };

    const iconFor = (tab: DataRoomTab): string => {
      if (!tab.definitionId) return "add_chart"; // generic icon for a new report
      const entry = (props.catalog as CatalogEntry[]).find(
        (c) => c.definitionId === tab.definitionId,
      );
      return entry?.icon ?? "insert_chart";
    };

    return () => (
      <div
        class="flex items-center gap-1.5 pt-1 border-b px-3 sm:px-3 overflow-visible"
        role="tablist"
      >
        {/*
          The tab strip is a real control surface now. The "+" was a ghost
          button at 50% opacity with no background or border — on a busy header
          it was effectively invisible until you happened to hover it.
        */}
        <button
          type="button"
          class="btn btn-xs"
          title={t("reports.dataroom.new_tab")}
          aria-label={t("reports.dataroom.new_tab")}
          onClick={() => props.onNew()}
        >
          <span class="material-symbols-outlined text-sm">add</span>
        </button>
        {(props.tabs as DataRoomTab[]).map((tab) => {
          const active = tab.id === props.activeTabId;
          return (
            // A tab was a <div> with a click handler, so it could not be
            // reached or activated from a keyboard at all.
            <div
              key={tab.id}
              class={[
                "group relative top-[1px] flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-t-lg border border-b-0 transition-colors relative",
                active
                  ? "top-[1px] text-accent bg-base-200 shadow-[0_-2px_6px_-2px_rgba(0,0,0,0.12)]"
                  : " ",
              ].join(" ")}
            >
              <button
                type="button"
                role="tab"
                aria-selected={active}
                class="flex items-center gap-1.5 rounded-t-lg py-1.5 pl-2.5 pr-0 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
                onClick={() => props.onSelect(tab.id)}
              >
                <span
                  class={[
                    "material-symbols-outlined text-base transition-colors",
                    active ? "text-primary" : "text-base-content/55",
                  ].join(" ")}
                >
                  {iconFor(tab)}
                </span>
                <span class="text-xs font-medium">{titleFor(tab)}</span>
              </button>
              {
                // Was opacity-0 until the parent was hovered, which meant it
                // did not exist for touch or keyboard users, and it had no
                // accessible name at all.
                <button
                  type="button"
                  class="mr-1.5 grid h-5 w-5 place-items-center rounded-md text-base-content/55 transition-colors hover:bg-error/15 hover:text-error focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                  aria-label={t("reports.dataroom.close_tab")}
                  title={t("reports.dataroom.close_tab")}
                  onClick={(ev: Event) => {
                    ev.stopPropagation();
                    props.onClose(tab.id);
                  }}
                >
                  <span class="material-symbols-outlined text-sm">close</span>
                </button>
              }
            </div>
          );
        })}
      </div>
    );
  },
});
